/**
 * Step2ContactDetails
 * Contact info and online presence: primary contact, phone numbers,
 * physical address, website, social media links, business hours.
 */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import BusinessHoursSelector from '../../components/BusinessHoursSelector';

const PROVINCES = [
  { value: '', label: 'Select province...' },
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_HOURS = DAYS.reduce((acc, day) => {
  acc[day] = {
    open: '09:00',
    close: '17:00',
    closed: day === 'Saturday' || day === 'Sunday',
  };
  return acc;
}, {});

export function getDefaultStep2Data() {
  return {
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    businessPhone: '',
    tollFreeNumber: '',
    streetAddress: '',
    addressCity: '',
    addressProvince: '',
    postalCode: '',
    website: '',
    linkedin: '',
    facebook: '',
    twitter: '',
    instagram: '',
    businessHours: DEFAULT_HOURS,
  };
}

export function validateStep2(data) {
  const errors = {};
  if (!data.contactName?.trim()) errors.contactName = 'Primary contact name is required';
  if (!data.contactEmail?.trim()) {
    errors.contactEmail = 'Contact email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.contactEmail)) {
    errors.contactEmail = 'Please enter a valid email address';
  }
  if (!data.contactPhone?.trim()) {
    errors.contactPhone = 'Contact phone is required';
  } else if (!/^[\d\s\-().+]{7,15}$/.test(data.contactPhone)) {
    errors.contactPhone = 'Please enter a valid phone number';
  }
  if (data.businessPhone && !/^[\d\s\-().+]{7,15}$/.test(data.businessPhone)) {
    errors.businessPhone = 'Please enter a valid phone number';
  }
  if (data.tollFreeNumber && !/^[\d\s\-().+]{7,15}$/.test(data.tollFreeNumber)) {
    errors.tollFreeNumber = 'Please enter a valid toll-free number';
  }
  if (data.postalCode && !/^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(data.postalCode)) {
    errors.postalCode = 'Enter a valid Canadian postal code (e.g., M5V 2T6)';
  }
  if (data.website && !/^https?:\/\/.+/.test(data.website)) {
    errors.website = 'Please enter a valid URL starting with http:// or https://';
  }
  const socialFields = ['linkedin', 'facebook', 'twitter', 'instagram'];
  socialFields.forEach((field) => {
    if (data[field] && !/^https?:\/\/.+/.test(data[field])) {
      errors[field] = 'Please enter a valid URL';
    }
  });
  return errors;
}

export default function Step2ContactDetails({ data, onChange, errors, touched, onBlur }) {
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    onChange({ ...data, [name]: value });
  }, [data, onChange]);

  const handleHoursChange = useCallback((hours) => {
    onChange({ ...data, businessHours: hours });
  }, [data, onChange]);

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={sectionTitleStyle}>Contact & Online Presence</h2>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          How can customers reach you? Provide your contact details and online presence.
        </p>
      </div>

      {/* Primary contact */}
      <section style={sectionStyle}>
        <h3 style={subTitleStyle}>Primary Contact</h3>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={labelStyle}>
              Contact Name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              name="contactName"
              value={data.contactName || ''}
              onChange={handleChange}
              onBlur={() => onBlur('contactName')}
              style={{ ...inputStyle, borderColor: touched.contactName && errors.contactName ? '#ef4444' : '#d1d5db' }}
              placeholder="John Smith"
            />
            {touched.contactName && errors.contactName && <div style={errorStyle}>{errors.contactName}</div>}
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={labelStyle}>
              Contact Email <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="email"
              name="contactEmail"
              value={data.contactEmail || ''}
              onChange={handleChange}
              onBlur={() => onBlur('contactEmail')}
              style={{ ...inputStyle, borderColor: touched.contactEmail && errors.contactEmail ? '#ef4444' : '#d1d5db' }}
              placeholder="john@company.ca"
            />
            {touched.contactEmail && errors.contactEmail && <div style={errorStyle}>{errors.contactEmail}</div>}
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={labelStyle}>
              Contact Phone <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="tel"
              name="contactPhone"
              value={data.contactPhone || ''}
              onChange={handleChange}
              onBlur={() => onBlur('contactPhone')}
              style={{ ...inputStyle, borderColor: touched.contactPhone && errors.contactPhone ? '#ef4444' : '#d1d5db' }}
              placeholder="(416) 555-1234"
            />
            {touched.contactPhone && errors.contactPhone && <div style={errorStyle}>{errors.contactPhone}</div>}
          </div>
        </div>

        {/* Business phone numbers */}
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={labelStyle}>Business Phone</label>
            <input
              type="tel"
              name="businessPhone"
              value={data.businessPhone || ''}
              onChange={handleChange}
              onBlur={() => onBlur('businessPhone')}
              style={{ ...inputStyle, borderColor: touched.businessPhone && errors.businessPhone ? '#ef4444' : '#d1d5db' }}
              placeholder="(416) 555-5678"
            />
            {touched.businessPhone && errors.businessPhone && <div style={errorStyle}>{errors.businessPhone}</div>}
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={labelStyle}>Toll-Free Number</label>
            <input
              type="tel"
              name="tollFreeNumber"
              value={data.tollFreeNumber || ''}
              onChange={handleChange}
              onBlur={() => onBlur('tollFreeNumber')}
              style={{ ...inputStyle, borderColor: touched.tollFreeNumber && errors.tollFreeNumber ? '#ef4444' : '#d1d5db' }}
              placeholder="1-800-555-0000"
            />
            {touched.tollFreeNumber && errors.tollFreeNumber && <div style={errorStyle}>{errors.tollFreeNumber}</div>}
          </div>
        </div>
      </section>

      {/* Physical address */}
      <section style={sectionStyle}>
        <h3 style={subTitleStyle}>Physical Address</h3>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Street Address</label>
          <input
            type="text"
            name="streetAddress"
            value={data.streetAddress || ''}
            onChange={handleChange}
            style={inputStyle}
            placeholder="123 Bay Street, Suite 400"
          />
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 180px' }}>
            <label style={labelStyle}>City</label>
            <input
              type="text"
              name="addressCity"
              value={data.addressCity || ''}
              onChange={handleChange}
              style={inputStyle}
              placeholder="Toronto"
            />
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <label style={labelStyle}>Province</label>
            <select
              name="addressProvince"
              value={data.addressProvince || ''}
              onChange={handleChange}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              {PROVINCES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div style={{ flex: '1 1 120px' }}>
            <label style={labelStyle}>Postal Code</label>
            <input
              type="text"
              name="postalCode"
              value={data.postalCode || ''}
              onChange={handleChange}
              onBlur={() => onBlur('postalCode')}
              style={{ ...inputStyle, borderColor: touched.postalCode && errors.postalCode ? '#ef4444' : '#d1d5db' }}
              placeholder="M5V 2T6"
            />
            {touched.postalCode && errors.postalCode && <div style={errorStyle}>{errors.postalCode}</div>}
          </div>
        </div>
      </section>

      {/* Website */}
      <section style={sectionStyle}>
        <h3 style={subTitleStyle}>Online Presence</h3>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Website URL</label>
          <input
            type="url"
            name="website"
            value={data.website || ''}
            onChange={handleChange}
            onBlur={() => onBlur('website')}
            style={{ ...inputStyle, borderColor: touched.website && errors.website ? '#ef4444' : '#d1d5db' }}
            placeholder="https://www.company.ca"
          />
          {touched.website && errors.website && <div style={errorStyle}>{errors.website}</div>}
        </div>

        {/* Social media */}
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          {[
            { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/...' },
            { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/...' },
            { key: 'twitter', label: 'Twitter / X', placeholder: 'https://x.com/...' },
            { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...' },
          ].map((social) => (
            <div key={social.key} style={{ flex: '1 1 220px' }}>
              <label style={labelStyle}>{social.label}</label>
              <input
                type="url"
                name={social.key}
                value={data[social.key] || ''}
                onChange={handleChange}
                onBlur={() => onBlur(social.key)}
                style={{ ...inputStyle, borderColor: touched[social.key] && errors[social.key] ? '#ef4444' : '#d1d5db' }}
                placeholder={social.placeholder}
              />
              {touched[social.key] && errors[social.key] && <div style={errorStyle}>{errors[social.key]}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* Business hours */}
      <section style={{ ...sectionStyle, marginBottom: 0 }}>
        <h3 style={subTitleStyle}>Business Hours</h3>
        <BusinessHoursSelector
          hours={data.businessHours || DEFAULT_HOURS}
          onChange={handleHoursChange}
        />
      </section>
    </div>
  );
}

Step2ContactDetails.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  onBlur: PropTypes.func.isRequired,
};

const sectionTitleStyle = {
  fontSize: '20px',
  fontWeight: '700',
  color: '#1e293b',
  marginBottom: '6px',
};

const subTitleStyle = {
  fontSize: '15px',
  fontWeight: '700',
  color: '#1e293b',
  marginBottom: '12px',
};

const sectionStyle = {
  backgroundColor: '#f8fafc',
  borderRadius: '10px',
  padding: '20px',
  marginBottom: '20px',
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '4px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '14px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  backgroundColor: '#fff',
  color: '#1e293b',
  outline: 'none',
  boxSizing: 'border-box',
};

const errorStyle = {
  fontSize: '12px',
  color: '#ef4444',
  marginTop: '3px',
};
